import DOMPurify from 'dompurify'
import type { DatosReporte } from './datos'

function v(val: any): string {
  return val !== null && val !== undefined ? String(val) : ''
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function formatFecha(dateStr: string): string {
  if (!dateStr) return ''
  try {
    const [datePart, timePart] = dateStr.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const fecha = new Date(year, month - 1, day).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' })
    const [hh, mm] = (timePart || '00:00').slice(0, 5).split(':').map(Number)
    const periodo = hh < 12 ? 'a.m.' : 'p.m.'
    const hora12 = hh % 12 === 0 ? 12 : hh % 12
    return `${fecha}, ${String(hora12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${periodo}`
  } catch { return dateStr }
}

function formatFechaSolo(dateStr: string): string {
  if (!dateStr) return ''
  try {
    const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number)
    return new Date(year, month - 1, day).toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch { return dateStr }
}

function esAcreditada(nombre: string): boolean {
  return nombre.startsWith('NMX') || nombre.startsWith('ANÁLISIS')
}

const CSS = `
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body { margin: 0; padding: 0; }
body { font-family: Arial, sans-serif; font-size: 9pt; color: #000; }
table { border-collapse: collapse; width: 100%; }
td, th { border: 1px solid #000; padding: 3px 6px; vertical-align: middle; font-size: 8.5pt; }
.bold { font-weight: bold; }
.center { text-align: center; }
.right { text-align: right; }
.small { font-size: 7.5pt; }
.text { position:absolute; z-index:3; white-space:nowrap; }
.content { padding: 0 30px 0; }
.seccion { margin-bottom: 10px; }
.no-break { page-break-inside: avoid; }
.pb { page-break-before: always; }
.pdf-embed { page-break-inside: auto; }
.pdf-embed tr { page-break-inside: auto !important; break-inside: auto !important; }
.pdf-embed td { border: 1px solid #aaa !important; }
.rc083-row { page-break-inside: auto !important; break-inside: auto !important; }
.no-break-header { break-inside: avoid; page-break-inside: avoid; }
.rc083-cumple { color: #166534; font-weight: bold; }
.rc083-no-cumple { color: #991b1b; font-weight: bold; }
.th-row td { font-weight: bold; background: #f0f0f0; font-size: 8.5pt; text-align: center; }
@page {
  size: letter;
  margin: 30mm 0 22mm 0;
  @bottom-center {
    content: counter(page) " de " counter(pages);
    font-size: 8pt;
    font-family: Arial, sans-serif;
    color: #000;
  }
}
@media print {
  tr { page-break-inside: avoid; break-inside: avoid; }
}
@media screen {
  .content { padding: 15px 30px; max-width: 820px; margin: 0 auto; }
}
`

function portada(p: Record<string, any>): string {
  const year = p.created_at ? new Date(p.created_at).getFullYear() : new Date().getFullYear()
  return `
  <div class="no-break">
    <div style="text-align:center;margin:18px 0 10px">
      <p class="bold" style="font-size:11pt">INFORME DE ENSAYOS</p>
      <p class="bold">(F-7.8-1 Rev.1)</p>
      <p class="small" style="margin-top:4px">Laboratorio de ensayo acreditado por ema, a.c., con acreditación No. ${v(p.no_acreditacion)} vigencia a partir del ${p.no_acreditacion === 'MM-1958-253/25' ? '2026-04-16' : '2026-04-10'}</p>
    </div>
    <div style="text-align:right;margin-bottom:12px">
      <p>NO. DE SOLICITUD: ${v(p.origen_numero)}</p>
      <p>NO. DE INFORME: ${p.informe_numero ? v(p.informe_numero) : `DL${year}-${v(p.folio)}`}</p>
      <p>FECHA DE INGRESO: ${formatFecha(p.created_at)}</p>
      <p>FECHA DE EMISIÓN: ${formatFechaSolo(p.fecha_entrega || p.fecha_emision || p.created_at)}</p>
    </div>
    <table>
      <tr><td colspan="2" class="center bold" style="padding:7px;font-size:10pt">DATOS PROPORCIONADOS POR EL CLIENTE</td></tr>
      <tr><td class="right bold" style="width:38%">NOMBRE O<br>RAZÓN SOCIAL:</td><td>${v(p.nombre_cliente)}</td></tr>
      <tr><td class="right bold">DIRECCIÓN:</td><td>${v(p.direccion)}</td></tr>
      <tr><td class="right bold">TELÉFONO:</td><td>${v(p.telefono)}</td></tr>
      <tr><td class="right bold">CONTACTO:</td><td>${v(p.contacto)}</td></tr>
      <tr><td class="right bold">NOMBRE DE LA<br>MUESTRA:</td><td>${v(p.muestra)}</td></tr>
      <tr><td class="right bold">CON ATENCIÓN A:</td><td>${v(p.con_atencion)}</td></tr>
    </table>
  </div>`
}

function leyendaResultados(): string {
  return `
  <div class="no-break">
    <p class="center bold" style="text-decoration:underline;margin:16px 0 10px;font-size:10pt">RESULTADOS DE LOS ENSAYOS</p>
    <p class="small" style="margin-bottom:5px">NOTAS: 1. Los ensayos fueron realizados entre la fecha de ingreso de la muestra y fecha de emisión del informe</p>
    <p class="small" style="margin-bottom:5px">2. Los resultados del presente informe corresponden a los métodos de análisis empleados y referidos exclusivamente de la muestra proporcionada por el cliente, ensayada tal y como fue recibida en las instalaciones del laboratorio</p>
    <p class="small">3. Queda prohibida la reproducción parcial o total del contenido del presente informe sin la autorización por escrito de DEXTERITE-LAB S.A. DE C.V.</p>
  </div>`
}

function cierre(p: Record<string, any>): string {
  const testigo = v(p.testigo)
  return `
  <div style="margin-top:25px">
    <p class="center" style="margin-bottom:14px;font-weight:bold">TESTIGO DEL ITEM DEL ENSAYO</p>
    <div style="text-align:center;margin-bottom:20px">
      ${testigo
        ? `<img src="${testigo}" style="max-width:420px;max-height:420px;border:1px solid #000">`
        : `<div style="width:420px;height:320px;border:1px solid #000;margin:0 auto"></div>`
      }
    </div>
    <p class="center bold" style="text-decoration:underline;margin-bottom:16px;font-size:11pt">FIN DEL INFORME DE ENSAYOS</p>
    <div class="no-break">
      <table>
        <tr>
          <td class="center bold" style="padding:10px;width:50%">SUPERVISÓ</td>
          <td class="center bold" style="padding:10px;width:50%">AUTORIZÓ</td>
        </tr>
        <tr>
          <td class="center" style="padding:18px">${v(p.superviso)}</td>
          <td class="center" style="padding:18px">${v(p.autorizo)}</td>
        </tr>
      </table>
    </div>
  </div>`
}

// ─── Secciones por prueba ──────────────────────────────────────────────────

const SUBNORMA_1833_MAP: Record<string, string> = {
  'NMX-A-1833-4':  'NMX-A-1833/4-INNTEX-2014 Mezcla de fibras proteicas y otras fibras (metodo con hipoclorito)',
  'NMX-A-1833-11': 'NMX-A-1833/11-INNTEX-2014 Mezcla de fibras de celulosa y poliéster (método usando ácido sulfúrico)',
  'NMX-A-1833-12': 'NMX-A-1833/12-INNTEX-2014 Mezcla de acrílico con ciertas modacrílicas, clorofibras, elastanos y otras fibras (método usando Dimetilformamida)',
}

function formatNormaAplicada(opcionNmx: string): string {
  if (!opcionNmx) return ''
  const parts = opcionNmx.split(',').map((s: string) => s.trim())
  return parts.map((p: string) => SUBNORMA_1833_MAP[p] || p).join(', ')
}

// CLIENTE ANALISIS — Tabla 2: 4 cols
function seccionAnalisis(d: any): string {
  const tablaApresto = (() => {
    if (d.pretratamiento !== 'Si' || (d.reportar_apresto !== 'reportar' && d.reportar_apresto !== 'solo_apresto')) return ''
    const p0 = parseFloat(d.p0 || '0')
    const pf = parseFloat(d.pf || '0')
    const pctA = p0 > 0 ? (((p0 - pf) / p0) * 100).toFixed(1) : '—'
    return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Análisis químico cuantitativo NMX-A-1833/1-INNTEX-2014</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="3">Pre-tratamiento especial Punto 8.2: ${v(d.sustancia)}</td><td>No. de ensayos realizados: 1</td></tr>
    <tr><td colspan="3">Tipo de composición: Total del material</td><td>Resultados basados en: Masa seca y limpia</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="4">${pctA}% materia no fibrosa (apresto)</td></tr>
  </table></div>`
  })()

  // Solo apresto: no se reporta la tabla de composición/fibras
  if (d.reportar_apresto === 'solo_apresto') return tablaApresto

  const is084 = String(d.opcion_nmx || '').includes('084')

  const tablaPrincipal = is084
    ? `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Análisis cualitativo-cuantitativo para una fibra 100% NMX-A-084-INNTEX-2015</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td>Procedimientos utilizados:</td><td colspan="3">Análisis por combustión, análisis microscópico y análisis químico</td></tr>
    <tr><td colspan="3">Tratamiento especial para remoción de apresto o acabado adicional al pre-tratamiento:</td><td>${d.pretratamiento === 'Si' ? 'Si' : 'Ninguno'}</td></tr>
    ${d.pretratamiento === 'Si' && v(d.sustancia) ? `<tr><td>Pre-tratamiento:</td><td colspan="3">${v(d.sustancia)}</td></tr>` : ''}
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="4" style="white-space:pre-wrap">${v(d.resultado_quimico)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
    : `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Análisis químico cuantitativo – Parte 1– Principios generales de ensayo NMX-A-1833/1-INNTEX-2014</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="4">Norma Aplicada: ${formatNormaAplicada(d.opcion_nmx)}</td></tr>
    <tr><td>Resultados basados en masa seca y limpia</td><td>Pre-tratamiento especial: ${v(d.pretratamiento)}${d.sustancia ? `, ${v(d.sustancia)}` : ''}</td><td colspan="2">No. de ensayos realizados: 1</td></tr>
    <tr><td colspan="4">Tipo de composición: Total del material</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="4" style="white-space:pre-wrap">${v(d.resultado_quimico)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`

  return tablaApresto + tablaPrincipal
}

// CLIENTE A-073 — Tabla 1: 3-col matching docx
function seccion073(d: any): string {
  if (d.reportar_cambio_color) {
    return `<div class="no-break seccion"><table>
      <tr class="th-row"><td colspan="4">Determinación de la solidez del color al frote NMX-A-073-INNTEX-2005</td><td class="center bold">ACREDITADA</td></tr>
      <tr><td colspan="5">FECHA: ${v(d.fecha)}</td></tr>
      <tr><td colspan="5">Método de evaluación: Escala de grises</td></tr>
      <tr><td colspan="5" class="center bold">RESULTADO</td></tr>
      <tr><td></td><td colspan="2" class="center bold">SECO</td><td colspan="2" class="center bold">HÚMEDO</td></tr>
      <tr><td></td><td class="center bold">GRADO DE TRANSFERENCIA</td><td class="center bold">GRADO DE CAMBIO DE COLOR</td><td class="center bold">GRADO DE TRANSFERENCIA</td><td class="center bold">GRADO DE CAMBIO DE COLOR</td></tr>
      <tr><td class="bold">URDIMBRE</td><td class="center">${v(d.seco_tu)}</td><td class="center">${v(d.seco_cu)}</td><td class="center">${v(d.humedo_tu)}</td><td class="center">${v(d.humedo_cu)}</td></tr>
      <tr><td class="bold">TRAMA</td><td class="center">${v(d.seco_tt)}</td><td class="center">${v(d.seco_ct)}</td><td class="center">${v(d.humedo_tt)}</td><td class="center">${v(d.humedo_ct)}</td></tr>
      ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="5" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
    </table></div>`
  }
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="2">Determinación de la solidez del color al frote NMX-A-073-INNTEX-2005</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="3">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="3">Método de evaluación: Escala de grises</td></tr>
    <tr><td colspan="3" class="center bold">RESULTADO</td></tr>
    <tr><td></td><td class="center bold">GRADO DE TRANSFERENCIA (SECO)</td><td class="center bold">GRADO DE TRANSFERENCIA (HUMEDO)</td></tr>
    <tr><td class="bold">URDIMBRE</td><td class="center">${v(d.seco_tu)}</td><td class="center">${v(d.humedo_tu)}</td></tr>
    <tr><td class="bold">TRAMA</td><td class="center">${v(d.seco_tt)}</td><td class="center">${v(d.humedo_tt)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="3" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-104 — Tabla 1: 4-col matching docx (all span 1)
function seccion104(d: any): string {
  const labelDiacetatoAc = v(d.diacetato_acido_label) || 'diacetato (acetato)'
  const labelDiacetatoAl = v(d.diacetato_alcalino_label) || 'diacetato (acetato)'
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Solidez del color- Determinación de la solidez del color al sudor NMX-A-105-E04-INNTEX-2019</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td>Multifibra: ${v(d.tipo_multifibra)}</td><td>Forma de evaluación: ${v(d.forma_evaluacion)}</td><td colspan="2">ENSAYO REALIZADO EN VERTICAL</td></tr>
    <tr><td colspan="4">Opción utilizada: ${v(d.metodo_c)}</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="2" class="center bold">ÁCIDO</td><td colspan="2" class="center bold">ALCALINO</td></tr>
    <tr><td class="center bold">FIBRA</td><td class="center bold">TRANSFERENCIA</td><td class="center bold">FIBRA</td><td class="center bold">TRANSFERENCIA</td></tr>
    <tr><td>${labelDiacetatoAc}</td><td class="center">${v(d.diacetato_ac)}</td><td>${labelDiacetatoAl}</td><td class="center">${v(d.diacetato_al)}</td></tr>
    <tr><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_ac)}</td><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_al)}</td></tr>
    <tr><td>poliamida</td><td class="center">${v(d.poliamida_ac)}</td><td>poliamida</td><td class="center">${v(d.poliamida_al)}</td></tr>
    <tr><td>poliéster</td><td class="center">${v(d.poliester_ac)}</td><td>poliéster</td><td class="center">${v(d.poliester_al)}</td></tr>
    <tr><td>acrílico</td><td class="center">${v(d.acrilico_ac)}</td><td>acrílico</td><td class="center">${v(d.acrilico_al)}</td></tr>
    <tr><td>lana</td><td class="center">${v(d.lana_ac)}</td><td>lana</td><td class="center">${v(d.lana_al)}</td></tr>
    <tr><td>CAMBIO DE COLOR</td><td class="center">${v(d.cambio_color_ac)}</td><td>CAMBIO DE COLOR</td><td class="center">${v(d.cambio_color_al)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-105 — Tabla 1: outer (12 cols) + nested result table matching docx
function seccion105(d: any): string {
  const labelDiacetato105 = v(d.diacetato_dw_label) || 'diacetato (acetato)'
  const labelTriacetato105 = v(d.diacetato_tv_label) || 'triacetato (acetato)'
  const resultNested = `<table style="width:100%;border-collapse:collapse">
    <tr><td colspan="2" class="center bold">TELA MULTIFIBRA DW</td><td colspan="2" class="center bold">TELA MULTIFIBRA TV</td></tr>
    <tr><td class="center bold">FIBRA</td><td class="center bold">TRANSFERENCIA</td><td class="center bold">FIBRA</td><td class="center bold">TRANSFERENCIA</td></tr>
    <tr><td>${labelDiacetato105}</td><td class="center">${v(d.diacetato_dw)}</td><td>${labelTriacetato105}</td><td class="center">${v(d.diacetato_tv)}</td></tr>
    <tr><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_dw)}</td><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_tv)}</td></tr>
    <tr><td>poliamida</td><td class="center">${v(d.poliamida_dw)}</td><td>poliamida</td><td class="center">${v(d.poliamida_tv)}</td></tr>
    <tr><td>poliéster</td><td class="center">${v(d.poliester_dw)}</td><td>poliéster</td><td class="center">${v(d.poliester_tv)}</td></tr>
    <tr><td>acrílico</td><td class="center">${v(d.acrilico_dw)}</td><td>acrílico</td><td class="center">${v(d.acrilico_tv)}</td></tr>
    <tr><td>lana</td><td class="center">${v(d.lana_dw)}</td><td>viscosa</td><td class="center">${v(d.lana_tv)}</td></tr>
    <tr><td>CAMBIO DE COLOR</td><td class="center">${v(d.cambio_color_dw)}</td><td>CAMBIO DE COLOR</td><td class="center">${v(d.cambio_color_tv)}</td></tr>
  </table>`
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="11">Solidez del color al lavado doméstico y comercial NMX-A-105-C06-INNTEX-2015</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="12">FECHA: ${v(d.fecha)}</td></tr>
    <tr>
      <td>Ensayo</td><td>${v(d.metodo_c)}</td>
      <td>Multifibra</td><td>${v(d.multifibra)}</td>
      <td>Temperatura</td><td>${v(d.temp_c)}</td>
      <td>Vol. De baño</td><td>${v(d.vol_bano_c)}</td>
      <td>Ajuste de pH</td><td>${v(d.ajuste_ph_c)}</td>
      <td>Secado</td><td>${v(d.secado_c)}</td>
    </tr>
    <tr>
      <td>Tiempo de lavado</td><td>${v(d.tiempo_c)}</td>
      <td>No. de balines</td><td>${v(d.balines_c)}</td>
      <td>Cloro activo (%)</td><td>${v(d.cloro_c)}</td>
      <td colspan="2">Tratamiento de acidulación</td><td colspan="4">${v(d.acidulacion)}</td>
    </tr>
    <tr>
      <td colspan="3">Detergente ${v(d.detergente_c)}</td>
      <td colspan="5">Forma de evaluación para la muestra y multifibra: Escala de gris</td>
      <td colspan="4">Desviaciones al método: ${v(d.desv_metodo)}</td>
    </tr>
    <tr><td colspan="12" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="12" style="padding:0;vertical-align:top">${resultNested}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="12" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-109 — Tabla 1: 7 cols
function seccion109(d: any): string {
  const estados = String(d.estado_espec_rr || '')
  const mostrarSecos = estados.includes('secos')
  const mostrarHumedos = estados.includes('humedos')
  // Compatibilidad con datos antiguos sin bandera de estado: usar las columnas "secos" por defecto
  const usarSecos = mostrarSecos || !mostrarHumedos

  const tablaResultados = (urdimbre: any[], trama: any[], promedioU: any, promedioT: any) => `
    <tr><td class="center bold">ESPECIMEN</td><td class="center bold">1</td><td class="center bold">2</td><td class="center bold">3</td><td class="center bold">4</td><td class="center bold">5</td><td class="center bold">PROMEDIO (N)</td></tr>
    <tr><td class="bold">URDIMBRE (N)</td>${urdimbre.map(val => `<td class="center">${v(val)}</td>`).join('')}<td class="center">${v(promedioU)} N</td></tr>
    <tr><td class="bold">TRAMA (N)</td>${trama.map(val => `<td class="center">${v(val)}</td>`).join('')}<td class="center">${v(promedioT)} N</td></tr>`

  let filas = ''
  if (usarSecos) {
    filas += tablaResultados(
      [d.no1_1, d.no2_1, d.no3_1, d.no4_1, d.no5_1],
      [d.no1_2, d.no2_2, d.no3_2, d.no4_2, d.no5_2],
      d.promedio_u || d.promedio1,
      d.promedio_t || d.promedio2
    )
  }
  if (mostrarHumedos) {
    filas += tablaResultados(
      [d.hum_u1, d.hum_u2, d.hum_u3, d.hum_u4, d.hum_u5],
      [d.hum_t1, d.hum_t2, d.hum_t3, d.hum_t4, d.hum_t5],
      d.hum_pu,
      d.hum_pt
    )
  }

  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="6">Determinación de la resistencia al rasgado por el método del péndulo de descenso libre NMX-A-109-INNTEX-2012</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="7">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td>Estado de los especímenes: ${v(d.estado_espec_rr)}</td><td colspan="2">Tipo de aparato: Digital</td><td>Material evaluado: TEXTIL</td><td colspan="3">Capacidad empleada Urdimbre: ${v(d.urdimbre)} Trama: ${v(d.trama)}</td></tr>
    <tr><td colspan="7">Número de pruebas rechazadas por fallas por rasgado transversal o por otras razones: ${v(d.espec_rechz_rr)}</td></tr>
    <tr><td colspan="7" class="center bold">RESULTADO</td></tr>
    ${filas}
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="7" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-12945 — Tabla 1: 4-col matching docx
function seccion12945(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Determinación de la propensión de la superficie de la tela al frisado, vellosidad o enmarañamiento NMX-A-12945-3-INNTEX-2020</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td>Fecha del ensayo: ${v(d.fecha)}</td><td>Número de especímenes: 3</td><td colspan="2">Forma de los especímenes: ${v(d.forma_espec)}</td></tr>
    <tr><td>Tipo de Revestimiento: ${v(d.tipo_revestimiento)}</td><td>Tiempo del ensayo (min): ${v(d.tiempo_ensayo)}</td><td colspan="2">Número de observadores: ${v(d.no_observadores)}</td></tr>
    <tr><td colspan="2">Pre-tratamiento (conforme a la NMX-A-6330-INNTEX-2015): Lavado: ${v(d.lavado)}</td><td colspan="2">Secado: ${v(d.secado)}</td></tr>
    <tr><td colspan="4">Dispositivo utilizado: ${v(d.dispositivo)}</td></tr>
    <tr><td colspan="2">Desviaciones al método: ${v(d.desv_metodo)}</td><td colspan="2" style="text-align:justify">Observación de los cambios en superficie o comportamiento de la tela: ${d._mostrarObs ? v(d.observaciones) : ''}</td></tr>
    <tr><td colspan="4">Los especímenes fueron evaluados con el apoyo de patrones de clasificación fotográfica</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td class="center bold">1ER CICLO</td><td class="center bold">5 MINUTOS (PASO 1)</td><td class="center bold">15 MINUTOS (PASO 2)</td><td class="center bold">30 MINUTOS (PASO 3)</td></tr>
    <tr><td>PROMEDIO FRISADO</td><td class="center">${v(d.prom_frisas5)}</td><td class="center">${v(d.prom_frisas15)}</td><td class="center">${v(d.prom_frisas30)}</td></tr>
    <tr><td>PROMEDIO VELLOSIDAD</td><td class="center">${v(d.prom_vellosidad5)}</td><td class="center">${v(d.prom_vellosidad15)}</td><td class="center">${v(d.prom_vellosidad30)}</td></tr>
    <tr><td>PROMEDIO ENMARAÑAMIENTO</td><td class="center">${v(d.prom_enmara5)}</td><td class="center">${v(d.prom_enmara15)}</td><td class="center">${v(d.prom_enmara30)}</td></tr>
    <tr><td class="center bold">2DO CICLO</td><td class="center bold">5 MINUTOS (PASO 1)</td><td class="center bold">15 MINUTOS (PASO 2)</td><td class="center bold">30 MINUTOS (PASO 3)</td></tr>
    <tr><td>PROMEDIO FRISADO</td><td class="center">${v(d.prom_frisas5_2)}</td><td class="center">${v(d.prom_frisas15_2)}</td><td class="center">${v(d.prom_frisas30_2)}</td></tr>
    <tr><td>PROMEDIO VELLOSIDAD</td><td class="center">${v(d.prom_vellosidad5_2)}</td><td class="center">${v(d.prom_vellosidad15_2)}</td><td class="center">${v(d.prom_vellosidad30_2)}</td></tr>
    <tr><td>PROMEDIO ENMARAÑAMIENTO</td><td class="center">${v(d.prom_enmara5_2)}</td><td class="center">${v(d.prom_enmara15_2)}</td><td class="center">${v(d.prom_enmara30_2)}</td></tr>
  </table></div>`
}

// CLIENTE A-134 — Tabla 1: 5 cols (all span 1)
function seccion134(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="4">Determinación de la densidad del tejido de punto y tolerancias NMX-A-134-INNTEX-2013</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">Condiciones ambientales (NMX-A-139-INNTEX-2014)</td><td>${v(d.grados)} °C</td><td>${v(d.hr)} % HR</td><td>Fecha del ensayo: ${v(d.fecha)}</td></tr>
    <tr><td>Tamaño del modelo repetido: ${v(d.t_modelo || d.tam_modelo)}</td><td colspan="2">Total de metros que constituya a los tejidos: ${v(d.total_metros)}</td><td colspan="2">Desviación al método: ${v(d.desv_metodo)}</td></tr>
    <tr><td>Tamaño de cada diseño componente del modelo: ${v(d.tam_modelo)}</td><td colspan="4"></td></tr>
    <tr><td colspan="5" class="center bold">RESULTADO</td></tr>
    <tr><td>Columnas por centímetro</td><td>${d.suma_c !== null && d.suma_c !== undefined ? Math.round(Number(d.suma_c)) : ''}</td><td colspan="2">Mallas por centímetro</td><td>${d.suma_m !== null && d.suma_m !== undefined ? Math.round(Number(d.suma_m)) : ''}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="5" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-172 — Tabla 1: 4-col
function seccion172(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Determinación de la resistencia a la abrasión plana (Taber) en los materiales textiles Método de prueba NMX-A-172-INNTEX-2012</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td>Carga (g): ${v(d.carga)}</td><td>Tipo de rueda: ${v(d.rueda_abrasiva)}</td><td>Nivel de vacío de succión (%): ${v(d.nivel_vacio)}</td><td>Altura de la boquilla de vacío (mm): 7 ± 1</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="3">Rotura del 1er hilo (no. de ciclos)</td><td>${v(d.resultado1)}</td></tr>
    <tr><td colspan="3">Cambio de Color:</td><td>${v(d.resultado2)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-221 — Tabla 1: 2-col matching docx
function seccion221(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Determinación de las grasas y otros materiales solubles por extracción con cloruro de metileno NMX-A-221-1982</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td>Detalles de cualquier variación:</td><td>${v(d.det_variacion)}</td></tr>
    <tr><td>% grasa y otros materiales solubles</td><td>${v(d.grasa)} %</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-225 — Tabla 1: 2-col matching docx
function seccion225(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Determinación de humedad en cuero NMX-A-225-1982</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td>Detalles de cualquier variación:</td><td>${v(d.det_variacion)}</td></tr>
    <tr><td>% de humedad</td><td>${v(d.humedad)} %</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-228 — Tabla 1: 2-col matching docx
function seccion228(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Determinación de cenizas totales y cenizas insolubles en agua NMX-A-228-1982</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td>Detalles de cualquier variación:</td><td>${v(d.det_variacion)}</td></tr>
    <tr><td>% de cenizas totales sulfatadas</td><td>${v(d.cenizas_totales)} %</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-229 — Tabla 1: 2-col matching docx
function seccion229(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>NMX-A-229-1982 Determinación del pH y Δ pH de un extracto acuoso de cuero</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td>Detalles de cualquier variación:</td><td>${v(d.det_variacion)}</td></tr>
    <tr><td>pH solución inicial (extracto de cuero)</td><td>${v(d.sol_inicial)}</td></tr>
    <tr><td>Δ pH</td><td>${v(d.ph)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-230 — Tabla 1: 2-col matching docx
function seccion230(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Determinación del contenido de cromo. Método: Ácido perclórico NMX-A-230-1982</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td>Detalles de cualquier variación:</td><td>${v(d.det_variacion)}</td></tr>
    <tr><td>% de cromo</td><td>${v(d.cromo)} %</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-275 — Tabla 1: 4 cols matching docx
function seccion275(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="3">Determinación de la densidad lineal de hilos extraídos de la tela NMX-A-275/5-INNTEX-2000</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2">Condiciones ambientales conforme a la NMX-A-139-INNTEX-2014:</td><td>${v(d.grados)} °C</td><td>${v(d.hr)} % HR</td></tr>
    <tr><td>Método utilizado: A</td><td colspan="3">Se  elimina la materia no fibrosa: ${v(d.mat_no_fibrosa)}</td></tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr><td class="bold">URDIMBRE</td><td>${v(d.no_tex_ur)} Tex</td><td class="bold">TRAMA</td><td>${v(d.no_tex_tr)} Tex</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-3801 — Tabla 1: 7 cols (ESPECIMEN + 5 + PROMEDIO)
function seccion3801(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="6">Determinación de la masa del tejido por unidad de longitud y área NMX-A-3801-INNTEX-2012</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="3">Método empleado: 5</td><td colspan="4">Fecha del ensayo: ${v(d.fecha)}</td></tr>
    <tr><td colspan="7">Desviación al método: ${v(d.desv_metodo)}</td></tr>
    <tr><td colspan="7" class="center bold">RESULTADO</td></tr>
    <tr><td class="center bold">ESPECIMEN</td><td class="center bold">1</td><td class="center bold">2</td><td class="center bold">3</td><td class="center bold">4</td><td class="center bold">5</td><td class="center bold">PROMEDIO</td></tr>
    <tr><td class="center bold">RESULTADO</td><td class="center">${v(d.no1_2)}</td><td class="center">${v(d.no2_2)}</td><td class="center">${v(d.no3_2)}</td><td class="center">${v(d.no4_2)}</td><td class="center">${v(d.no5_2)}</td><td class="center">${d.promedio !== null && d.promedio !== undefined ? Math.round(Number(d.promedio)) : ''} g/m²</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="7" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-4920 — Tabla 1: 5-col
function seccion4920(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="4">Determinación de la resistencia al humedecimiento superficial (Método de rocío) NMX-A-4920-INNTEX-2017</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="5">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2">Condiciones ambientales conforme a la NMX-A-139-INNTEX-2014:</td><td>${v(d.grados)} °C</td><td>${v(d.hr)} % HR</td><td>Temperatura del agua: ${v(d.temp_agua)}</td></tr>
    <tr><td colspan="5">Desviación al método: ${v(d.desv_metodo)}</td></tr>
    <tr><td colspan="5" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="2" class="center bold">ESPECIMEN</td><td colspan="3" class="center bold">GRADO DE HUMEDECIMIENTO</td></tr>
    <tr><td colspan="2" class="center">1</td><td colspan="3" class="center">${v(d.humed1)}</td></tr>
    <tr><td colspan="2" class="center">2</td><td colspan="3" class="center">${v(d.humed2)}</td></tr>
    <tr><td colspan="2" class="center">3</td><td colspan="3" class="center">${v(d.humed3)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="5" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-5077 — Tabla 1: 7-col matching docx
function seccion5077(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="6">Determinación del cambio dimensional en el lavado y secado NMX-A-5077-INNTEX-2015</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="7">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="7">Los especímenes fueron medidos y marcados conforme a la norma NMX-A-3759-INNTEX-2011</td></tr>
    <tr><td colspan="4">Dimensiones de los especímenes: Lienzo de tela ${v(d.dim_especim)}</td><td colspan="3">No. de especímenes lavados y secados: 2</td></tr>
    <tr><td colspan="2">Procedimiento de lavado conforme a la NMX-A-6330-INNTEX-2015:</td><td colspan="2">Lavado: ${v(d.lavado)}</td><td colspan="3">Secado: ${v(d.secado)}</td></tr>
    <tr><td colspan="2">Tipo de lavadora: Tipo B</td><td>Tipo de secadora:</td><td colspan="2">${v(d.tipo_secadora)}</td><td colspan="2">Masa total de la carga: 2 kg ± 0.1</td></tr>
    <tr><td colspan="3">Tipo de detergente: WOB AATCC-1993</td><td colspan="4">Tela de carga utilizada: ${v(d.tipo_carga)}</td></tr>
    <tr><td class="bold">RESULTADO:</td><td class="bold">URDIMBRE:</td><td>${v(d.redondeo_uc)}</td><td>%</td><td class="bold">TRAMA:</td><td>${v(d.redondeo_tm)}</td><td>%</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="7" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-7211 — 13 cols (referencia: CLIENTE A-7211.docx)
function seccion7211(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="10">Determinación del número de hilos por unidad de longitud NMX-A-7211/2-INNTEX-2015</td><td colspan="3" class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="13">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="5">Condiciones ambientales conforme a la NMX-A-139-INNTEX-2014</td><td colspan="2">${v(d.grados)} °C</td><td colspan="3">${v(d.hr)} % HR</td><td colspan="3">Método A</td></tr>
    <tr><td colspan="3">Mediciones efectuadas: ${v(d.espec_u_ma)} en U y ${v(d.espec_t_ma || d.espec_u_ma)} en T.</td><td colspan="3">Distancia de medición:</td><td>U:</td><td colspan="2">${v(d.urdimbre)}</td><td>cm</td><td>T:</td><td>${v(d.trama)}</td><td>cm</td></tr>
    <tr><td colspan="13">Desviación al método: ${v(d.desv_metodo)}</td></tr>
    <tr><td colspan="13" class="center bold">RESULTADO</td></tr>
    <tr><td>URDIMBRE</td><td>${v(d.suma_u)}</td><td colspan="2">Hilos por centímetro</td><td colspan="2">TRAMA</td><td colspan="2">${v(d.suma_t)}</td><td colspan="5">Hilos por centímetro</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="13" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-9073 — Tabla 1: 7-col (ESPECIMEN + 5 + PROMEDIO)
function seccion9073(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="6">Determinación de la masa por unidad de longitud y de área NMX-A-9073/1-INNTEX-2018</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="7">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2">Condiciones ambientales conforme a la NMX-A-139-INNTEX-2014:</td><td colspan="2">${v(d.grados)} °C / ${v(d.hr)} % HR</td><td colspan="3">Coeficiente de variación: ${v(d.coef_variacion)}</td></tr>
    <tr><td colspan="7">Desviación al método: ${v(d.desv_metodo)}</td></tr>
    <tr><td colspan="7" class="center bold">RESULTADO</td></tr>
    <tr><td class="center bold">ESPECIMEN</td><td class="center bold">1</td><td class="center bold">2</td><td class="center bold">3</td><td class="center bold">4</td><td class="center bold">5</td><td class="center bold">PROMEDIO</td></tr>
    <tr><td class="center bold">RESULTADO</td><td class="center">${v(d.no1_2)}</td><td class="center">${v(d.no2_2)}</td><td class="center">${v(d.no3_2)}</td><td class="center">${v(d.no4_2)}</td><td class="center">${v(d.no5_2)}</td><td class="center">${v(d.promedio)} g/m2</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="7" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// MET-7.2-11 — 2-col table matching docx
function seccion711(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Método interno para la determinación del tipo de tejido MET-7.2-11</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="2">${v(d.resultado)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// MET-7.2-12 — 2 cols (referencia: CLIENTE 7.2-12.docx)
function seccion712(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Método interno para la determinación del tipo de ligamento MET-7.2-12</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="2">${v(d.res_plano_lig)} ${v(d.res_punto_lig)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// MET-7.2-19 — 2 cols (referencia: CLIENTE 7.2-19.docx)
function seccion719(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td>Método interno para la determinación del tipo de acabado MET-7.2-19</td><td class="center bold">ACREDITADA</td></tr>
    <tr><td colspan="2">FECHA: ${v(d.fecha)}</td></tr>
    <tr><td colspan="2" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="2">${v(d.resultado)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="2" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// NMX-A-083 — generado desde datos estructurados
function seccion083(d: any): string {
  const encabezado = `<table class="no-break">
      <tr class="th-row">
        <td colspan="6">NMX-A-083-INNTEX-2020 EVALUACIÓN DE CONFECCIÓN Y DIMENSIÓN DE PRENDAS</td>
        <td class="center bold">ACREDITADA</td>
      </tr>
      <tr>
        <td class="bold" style="width:14%">NO. DE SOLICITUD:</td><td style="width:19%;text-align:center">${v(d.numero_solicitud)}</td>
        <td class="bold" style="width:14%">FECHA:</td><td style="width:19%;text-align:center">${v(d.fecha)}</td>
        <td class="bold" style="width:14%">HORA:</td><td colspan="2" style="text-align:center">${v(d.hora)}</td>
      </tr>
      <tr>
        <td class="bold">°C:</td><td style="text-align:center">${v(d.temperatura)}</td>
        <td class="bold">%HR:</td><td style="text-align:center">${v(d.humedad)}</td>
        <td class="bold">ID TERMOHIGRÓMETRO:</td><td colspan="2" style="text-align:center">${v(d.id_termohigrometro)}</td>
      </tr>
      <tr>
        <td class="bold" colspan="2">ESPECIFICACIÓN DEL CLIENTE:</td>
        <td colspan="5" style="text-align:justify">${v(d.especificacion)}</td>
      </tr>
      <tr>
        <td class="bold">PRENDA:</td><td colspan="6" style="text-align:justify">${v(d.prenda)}</td>
      </tr>
      <tr>
        <td class="bold">REALIZÓ:</td><td colspan="2" style="text-align:center">${v(d.realizo)}</td>
        <td class="bold">SUPERVISÓ:</td><td colspan="3" style="text-align:center">${v(d.superviso)}</td>
      </tr>
    </table>`

  const ORDEN_SECCIONES_083 = ['descripcion', 'dimensiones', 'corte_confeccion', 'terminado', 'tipo_puntadas', 'tipos_hilos', 'etiquetado']
  const tieneContenido083 = (key: string): boolean => {
    switch (key) {
      case 'descripcion':      return Array.isArray(d.descripcion_tabla) && d.descripcion_tabla.length > 0
      case 'dimensiones':      return Array.isArray(d.dimensiones) && d.dimensiones.length > 0
      case 'corte_confeccion': return Array.isArray(d.confeccion) && d.confeccion.length > 0
      case 'terminado':        return Array.isArray(d.terminado) && d.terminado.length > 0
      case 'tipo_puntadas':    return Array.isArray(d.tipo_puntadas) && d.tipo_puntadas.length > 0
      case 'tipos_hilos':      return Array.isArray(d.tipos_hilos) && d.tipos_hilos.length > 0
      case 'etiquetado':       return Array.isArray(d.etiquetado) && d.etiquetado.length > 0
      default: return false
    }
  }
  const seccionesActivas = ORDEN_SECCIONES_083.filter(key => tieneContenido083(key))

  const norma = v(d.norma)
  const tituloStyle = 'font-weight:bold;text-align:center;background:#e8e8e8;padding:4px'
  const thStyle = 'font-weight:bold;text-align:center;padding:3px;white-space:nowrap'
  const notaDiv = (nota: string | null | undefined) =>
    nota ? `<div style="margin:4px 0 8px 0;padding:4px 6px;font-size:9pt;break-inside:avoid;page-break-inside:avoid">${DOMPurify.sanitize(nota)}</div>` : ''
  const conf = (val: string) => {
    if (val === 'cumple') return '<span style="color:#166534;font-weight:bold">CUMPLE</span>'
    if (val === 'no_cumple') return '<span style="color:#991b1b;font-weight:bold">NO CUMPLE</span>'
    return '—'
  }

  const tabla3col = (titulo: string, headerLeft: string, rows: { criteria: string; conformidad: string; comentario?: string; mostrar_comentario?: boolean }[]) => {
    const filas = rows.length === 0
      ? `<tr><td colspan="2" class="center">Sin datos</td></tr>`
      : rows.map(r => `<tr>
          <td style="padding:3px 4px;text-align:justify">${r.criteria}</td>
          <td style="text-align:center;padding:3px">${conf(r.conformidad)}</td>
        </tr>${r.comentario && r.mostrar_comentario ? `<tr><td colspan="2" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>` : ''}`).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="2" style="${tituloStyle}">${titulo}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">${headerLeft}</td>
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const tablaDescripcion = () => {
    const rows = (Array.isArray(d.descripcion_tabla) ? d.descripcion_tabla : [])
      .filter((r: any) => v(r.punto_medicion))
      .map((r: any) => ({ criteria: v(r.punto_medicion), conformidad: r.conformidad || '', comentario: r.comentario, mostrar_comentario: r.mostrar_comentario }))
    return tabla3col(v(d.titulo_descripcion) || 'DESCRIPCION', 'DESCRIPCIÓN DE LA CONFECCIÓN', rows)
  }

  const tablaDimensiones = () => {
    const rows: any[] = (Array.isArray(d.dimensiones) ? d.dimensiones : []).filter((r: any) => v(r.punto_medicion))
    const titulo = v(d.titulo_dimensiones) || 'DIMENSIONES'
    const tieneExtra = rows.some((r: any) => v(r.medicion) || v(r.especificacion))
    const cols = tieneExtra ? 4 : 2
    const extraHeaders = tieneExtra
      ? `<td style="${thStyle}">MEDICIÓN (cm)</td><td style="${thStyle}">ESPECIFICACIÓN (cm)</td>`
      : ''
    const filas = rows.length === 0
      ? `<tr><td colspan="${cols}" class="center">Sin datos</td></tr>`
      : rows.map((r: any) => {
          const nota = r.comentario && r.mostrar_comentario
            ? `<tr><td colspan="${cols}" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>`
            : ''
          const confVal = v(r.especificacion).toLowerCase() === 'no aplica' ? '—' : conf(r.conformidad)
          if (tieneExtra)
            return `<tr><td style="padding:3px 4px;text-align:justify">${v(r.punto_medicion)}</td><td style="text-align:center;padding:3px">${v(r.medicion)}</td><td style="text-align:center;padding:3px">${v(r.especificacion)}</td><td style="text-align:center;padding:3px">${confVal}</td></tr>${nota}`
          return `<tr><td style="padding:3px 4px;text-align:justify">${v(r.punto_medicion)}</td><td style="text-align:center;padding:3px">${confVal}</td></tr>${nota}`
        }).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="${cols}" style="${tituloStyle}">${titulo}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">PUNTO DE MEDICIÓN</td>
        ${extraHeaders}
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const tablaCorteConfeccion = () => {
    const rows: any[] = (Array.isArray(d.confeccion) ? d.confeccion : []).filter((r: any) => v(r.evaluacion))
    const tieneExtra = rows.some((r: any) => v(r.medicion) || v(r.especificacion))
    const cols = tieneExtra ? 4 : 2
    const extraHeaders = tieneExtra
      ? `<td style="${thStyle}">MEDICIÓN</td><td style="${thStyle}">ESPECIFICACIÓN</td>`
      : ''
    const filas = rows.length === 0
      ? `<tr><td colspan="${cols}" class="center">Sin datos</td></tr>`
      : rows.map((r: any) => {
          const nota = r.comentario && r.mostrar_comentario
            ? `<tr><td colspan="${cols}" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>`
            : ''
          const confVal = v(r.especificacion).toLowerCase() === 'no aplica' ? '—' : conf(r.conformidad)
          if (tieneExtra && !v(r.medicion) && !v(r.especificacion))
            return `<tr><td colspan="3" style="padding:3px 4px;text-align:justify">${v(r.evaluacion)}</td><td style="text-align:center;padding:3px">${confVal}</td></tr>${nota}`
          if (tieneExtra)
            return `<tr><td style="padding:3px 4px;text-align:justify">${v(r.evaluacion)}</td><td style="text-align:center;padding:3px">${v(r.medicion)}</td><td style="text-align:center;padding:3px">${v(r.especificacion)}</td><td style="text-align:center;padding:3px">${confVal}</td></tr>${nota}`
          return `<tr><td style="padding:3px 4px;text-align:justify">${v(r.evaluacion)}</td><td style="text-align:center;padding:3px">${confVal}</td></tr>${nota}`
        }).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="${cols}" style="${tituloStyle}">${v(d.titulo_confeccion) || 'CORTE Y CONFECCIÓN'}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">EVALUACIÓN DE LA CONFECCIÓN</td>
        ${extraHeaders}
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const tablaTipoPuntadas = () => {
    const rows = (Array.isArray(d.tipo_puntadas) ? d.tipo_puntadas : [])
      .filter((r: any) => v(r.descripcion))
      .map((r: any) => ({ criteria: v(r.descripcion), conformidad: r.conformidad || '', comentario: r.comentario, mostrar_comentario: r.mostrar_comentario }))
    return tabla3col(v(d.titulo_tipo_puntadas) || 'TIPO DE PUNTADAS', 'EVALUACIÓN DE LAS PUNTADAS', rows)
  }

  const tablaTiposHilos = () => {
    const rows: any[] = (Array.isArray(d.tipos_hilos) ? d.tipos_hilos : []).filter((r: any) => v(r.descripcion))
    const filas = rows.length === 0
      ? `<tr><td colspan="2" class="center">Sin datos</td></tr>`
      : rows.map((r: any) => {
          const nota = r.comentario && r.mostrar_comentario
            ? `<tr><td colspan="2" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>`
            : ''
          return `<tr>
            <td style="padding:3px 4px;text-align:justify">${v(r.descripcion)}</td>
            <td style="text-align:center;padding:3px">${conf(r.conformidad)}</td>
          </tr>${nota}`
        }).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="2" style="${tituloStyle}">${v(d.titulo_tipos_hilos) || 'EVALUACIÓN DEL TIPO DE HILO DE COSTURA'}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">CRITERIO</td>
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const tablaTerminado = () => {
    const rows: any[] = (Array.isArray(d.terminado) ? d.terminado : []).filter((r: any) => v(r.descripcion))
    const filas = rows.length === 0
      ? `<tr><td colspan="2" class="center">Sin datos</td></tr>`
      : rows.map((r: any) => {
          const nota = r.comentario && r.mostrar_comentario
            ? `<tr><td colspan="2" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>`
            : ''
          return `<tr>
            <td style="padding:3px 4px;text-align:justify">${v(r.descripcion)}</td>
            <td style="text-align:center;padding:3px">${conf(r.conformidad)}</td>
          </tr>${nota}`
        }).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="2" style="${tituloStyle}">${v(d.titulo_terminado) || 'EVALUACIÓN DEL TERMINADO DE LA PRENDA'}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">CRITERIO</td>
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const tablaEtiquetado = () => {
    const rows: any[] = (Array.isArray(d.etiquetado) ? d.etiquetado : []).filter((r: any) => v(r.descripcion))
    const filas = rows.length === 0
      ? `<tr><td colspan="2" class="center">Sin datos</td></tr>`
      : rows.map((r: any) => {
          const nota = r.comentario && r.mostrar_comentario
            ? `<tr><td colspan="2" style="font-style:italic;font-size:7.5pt;background:#f9f9f9;text-align:justify"><strong style="font-style:normal">Observación:</strong> ${v(r.comentario)}</td></tr>`
            : ''
          return `<tr>
            <td style="padding:3px 4px;text-align:justify">${v(r.descripcion)}</td>
            <td style="text-align:center;padding:3px">${conf(r.conformidad)}</td>
          </tr>${nota}`
        }).join('')
    return `<table style="margin-top:4px">
      <tr><td colspan="2" style="${tituloStyle}">${v(d.titulo_etiquetado) || 'NOM-004-SE-2021 Información comercial-Etiquetado de productos textiles, prendas de vestir, sus accesorios y ropa de casa — INSPECCIÓN DEL ETIQUETADO — PRUEBA NO ACREDITADA'}</td></tr>
      <tr>
        <td style="font-weight:bold;padding:3px 4px;text-align:center">CRITERIO</td>
        <td style="${thStyle}">CONFORMIDAD</td>
      </tr>
      ${filas}
    </table>`
  }

  const seccionesHtml = seccionesActivas.map(key => {
    switch (key) {
      case 'descripcion':      return tablaDescripcion() + notaDiv(d.nota_descripcion)
      case 'dimensiones':      return tablaDimensiones() + notaDiv(d.nota_dimensiones)
      case 'corte_confeccion': return tablaCorteConfeccion() + notaDiv(d.nota_confeccion)
      case 'terminado':        return tablaTerminado() + notaDiv(d.nota_terminado)
      case 'tipo_puntadas':    return tablaTipoPuntadas() + notaDiv(d.nota_tipo_puntadas)
      case 'tipos_hilos':      return tablaTiposHilos() + notaDiv(d.nota_tipos_hilos)
      case 'etiquetado':       return tablaEtiquetado() + notaDiv(d.nota_etiquetado)
      default: return ''
    }
  }).join('')

  const claveDiv = d.clave
    ? `<div style="margin:4px 0 8px;padding:4px 6px"><strong><em>CLAVE: ${escapeHtml(v(d.clave))}</em></strong></div>`
    : ''

  return `<div class="seccion">
    <div style="break-inside:avoid">
      ${encabezado}
      ${claveDiv}
      ${notaDiv(d.nota_doc)}
      ${notaDiv(d.nota_general)}
    </div>
    ${seccionesHtml}
  </div>`
}

// CLIENTE A-105-D01 — Solidez del color al lavado en seco
function seccion105d01(d: any): string {
  const multifibra = v(d.multifibra)
  const mostrarDW = !multifibra || multifibra === 'DW' || multifibra === 'DW y TV'
  const mostrarTV = !multifibra || multifibra === 'TV' || multifibra === 'DW y TV'

  const dwTable = mostrarDW ? `<table style="border-collapse:collapse;width:100%">
      <tr><td class="center bold">TELA MULTIFIBRA DW</td><td class="center bold">Transferencia</td></tr>
      <tr><td>diacetato</td><td class="center">${v(d.diacetato_dw)}</td></tr>
      <tr><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_dw)}</td></tr>
      <tr><td>poliamida</td><td class="center">${v(d.poliamida_dw)}</td></tr>
      <tr><td>poliéster</td><td class="center">${v(d.poliester_dw)}</td></tr>
      <tr><td>acrílico</td><td class="center">${v(d.acrilico_dw)}</td></tr>
      <tr><td>LANA</td><td class="center">${v(d.lana_dw)}</td></tr>
      <tr><td class="bold">CAMBIO DE COLOR</td><td class="center bold">${v(d.cambio_color_dw)}</td></tr>
    </table>` : ''

  const tvTable = mostrarTV ? `<table style="border-collapse:collapse;width:100%">
      <tr><td class="center bold">TELA MULTIFIBRA TV</td><td class="center bold">Transferencia</td></tr>
      <tr><td>triacetato</td><td class="center">${v(d.triacetato_tv)}</td></tr>
      <tr><td>ALGODÓN blanqueado</td><td class="center">${v(d.algodon_tv)}</td></tr>
      <tr><td>poliamida</td><td class="center">${v(d.poliamida_tv)}</td></tr>
      <tr><td>poliéster</td><td class="center">${v(d.poliester_tv)}</td></tr>
      <tr><td>acrílico</td><td class="center">${v(d.acrilico_tv)}</td></tr>
      <tr><td>viscosa</td><td class="center">${v(d.viscosa_tv)}</td></tr>
      <tr><td class="bold">CAMBIO DE COLOR</td><td class="center bold">${v(d.cambio_color_tv)}</td></tr>
    </table>` : ''

  const ambas = mostrarDW && mostrarTV
  const resultContent = ambas
    ? `<table style="width:100%;border-collapse:collapse">
        <tr>
          <td style="width:50%;padding:0;vertical-align:top;border:1px solid #000">${dwTable}</td>
          <td style="width:50%;padding:0;vertical-align:top;border:1px solid #000">${tvTable}</td>
        </tr>
      </table>`
    : `<div style="width:60%;margin:0 auto">${mostrarDW ? dwTable : tvTable}</div>`

  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="3" class="center bold">Solidez del color al lavado en seco utilizando solvente percloroetileno<br>NMX-A-105-D01-INNTEX-2020</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr>
      <td>Fecha</td>
      <td>${v(d.fecha)}</td>
      <td>Iluminante utilizado para la evaluación:</td>
      <td class="center bold">D65</td>
    </tr>
    <tr>
      <td>Solvente utilizado</td>
      <td class="center bold">${v(d.solvente) || 'Percloroetileno'}</td>
      <td>Evaluación para el ensayo:</td>
      <td class="bold">Escala de grises</td>
    </tr>
    <tr>
      <td>Desviación al método:</td>
      <td colspan="3">${v(d.desv_metodo)}</td>
    </tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr>
      <td colspan="4" style="padding:4px;border:none">
        ${resultContent}
      </td>
    </tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE A-105-B02 — Solidez del color a la luz artificial: arco de xenón
function seccion105b02(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="5" class="center bold">Solidez del color a la luz artificial- Parte B02: ensayo de la lámpara de<br>decoloración de arco de xenón (Método 5) &nbsp;NMX-A-105-B02-INNTEX-2019</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr><td colspan="6">FECHA: ${v(d.fecha)}</td></tr>
    <tr>
      <td>Método de evaluación:</td><td class="bold">Escala de grises</td>
      <td>Tipo de aparato:</td><td class="bold">Enfriado por agua</td>
      <td>Humedad relativa:</td><td class="bold">${v(d.humedad_relativa)}${v(d.humedad_relativa) ? ' %' : ''}</td>
    </tr>
    <tr>
      <td>Temperatura del panel negro:</td><td class="bold">${v(d.temp_panel_negro)}${v(d.temp_panel_negro) ? ' °C' : ''}</td>
      <td>Temperatura del éstandar negro:</td><td class="bold">${v(d.temp_estandar_negro)}${v(d.temp_estandar_negro) ? ' °C' : ''}</td>
      <td>Energía radiante:</td><td class="bold">${v(d.exposicion_radiante)}${v(d.exposicion_radiante) ? ' kJ/m²' : ''}</td>
    </tr>
    <tr>
      <td>Se utilizó modo oscilante:</td><td class="bold">No</td>
      <td>Irradiación:</td><td class="bold">${v(d.irradiacion)}${v(d.irradiacion) ? ' W/m²' : ''}</td>
      <td>Iluminante utilizado para la evaluación:</td><td class="bold">D65</td>
    </tr>
    <tr>
      <td colspan="3">Condiciones de exposición a la que se efectuó el ensayo</td>
      <td colspan="3" class="center bold">${v(d.ciclo_exposicion)}</td>
    </tr>
    <tr><td colspan="6" class="center bold">RESULTADO</td></tr>
    <tr><td colspan="6" class="center">CAMBIO DE COLOR</td></tr>
    <tr><td colspan="6" class="center">${v(d.grado_escala_gris)}</td></tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="6" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

function rCalcDesv(vals: string[]): number {
  const nums = vals.map((x: string) => parseFloat(x)).filter((n: number) => !isNaN(n))
  if (nums.length < 2) return 0
  const mean = nums.reduce((a: number, b: number) => a + b, 0) / nums.length
  return Math.sqrt(nums.reduce((s: number, x: number) => s + Math.pow(x - mean, 2), 0) / (nums.length - 1))
}
function rCV059_2(d: any, dir: 'u'|'t'): string {
  const prom = parseFloat(dir === 'u' ? d.promedio_u : d.promedio_t)
  const desv = rCalcDesv([1,2,3,4,5].map(i => d[`no${i}_${dir}`] || ''))
  if (!prom || !desv || isNaN(prom)) return ''
  return ((desv / prom) * 100).toFixed(2)
}

// CLIENTE A-059/2 — Fuerza máxima por el método de agarre (Grab)
function seccion059_2(d: any): string {
  const estado = typeof d.estado_especimenes === 'string'
    ? d.estado_especimenes.split(',').map((s: string) => s.trim()).filter(Boolean)
    : Array.isArray(d.estado_especimenes) ? d.estado_especimenes : []
  const estadoStr = estado.length > 0 ? estado.join(', ') : v(d.estado_especimenes)
  const rechazados = v(d.especimenes_rechazados)
  const hayRechazados = rechazados && rechazados !== '0' && rechazados !== ''
  const espU = v(d.especimenes_u)
  const espT = v(d.especimenes_t)
  const espStr = (espU || espT) ? `${espU || '—'} en Urdimbre y ${espT || '—'} en Trama` : ''
  const nota = d._mostrarObs ? v(d.observaciones) : ''
  const reportaCV = d.reportar !== false && d.reportar !== 'false'

  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="5">Propiedades de los tejidos frente a la tracción-Parte 2: Determinación de la fuerza máxima por el método de agarre-Método Grab.<br>NMX-059/2-INNTEX-2019</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr>
      <td>Fecha de ensayo:</td>
      <td><strong>${v(d.fecha)}</strong></td>
      <td colspan="2">Número de especímenes ensayados:</td>
      <td colspan="2">${espStr}</td>
    </tr>
    <tr>
      <td>Longitud del ensayo:</td>
      <td><strong>${v(d.longitud_ensayo)} mm</strong></td>
      <td>Especímenes rechazados:</td>
      <td>${hayRechazados ? rechazados : 'Ninguno'}</td>
      <td>Motivo:</td>
      <td>${hayRechazados ? '' : 'No aplica'}</td>
    </tr>
    <tr>
      <td colspan="2">Estado de los especímenes: <strong>${estadoStr}</strong></td>
      <td colspan="2">Desviación al método:</td>
      <td colspan="2"><strong>${v(d.desv_metodo) || 'Ninguna'}</strong></td>
    </tr>
    ${d.tiene_metodo_muestreo ? `<tr>
      <td colspan="2">Método de toma de muestreo:</td>
      <td colspan="4"><strong>${v(d.metodo_muestreo)}</strong></td>
    </tr>` : ''}
    <tr><td colspan="6" class="center bold">RESULTADO</td></tr>
    <tr>
      <td colspan="2" class="center">Urdimbre</td>
      <td colspan="1" class="center bold">${v(d.redondeo_u) ? `${v(d.redondeo_u)} N` : ''}</td>
      <td colspan="2" class="center">Trama</td>
      <td colspan="1" class="center bold">${v(d.redondeo_t) ? `${v(d.redondeo_t)} N` : ''}</td>
    </tr>
    ${reportaCV ? `<tr>
      <td colspan="3" class="center">CV Urdimbre: <strong>${rCV059_2(d, 'u')}%</strong></td>
      <td colspan="3" class="center">CV Trama: <strong>${rCV059_2(d, 't')}%</strong></td>
    </tr>` : ''}
    <tr><td colspan="6" style="text-align:justify">Notas: ${nota}</td></tr>
  </table></div>`
}

// CLIENTE A-059/1 — Fuerza máxima y alargamiento por el método de la tira
function rCV059_1(d: any, t: string, dir: 'u'|'t'): string {
  const prom = parseFloat(dir === 'u' ? d[`${t}_prom_u`] : d[`${t}_prom_t`])
  const desv = rCalcDesv([1,2,3,4,5].map(i => d[`${t}_${dir}${i}`] || ''))
  if (!prom || !desv || isNaN(prom)) return ''
  return ((desv / prom) * 100).toFixed(2)
}

function seccion059_1(d: any): string {
  const tablas = typeof d.tablas_activas === 'string'
    ? d.tablas_activas.split(',').map((s: string) => s.trim()).filter(Boolean)
    : Array.isArray(d.tablas_activas) ? d.tablas_activas : ['fm', 'fr', 'afm', 'ar']

  const estado = typeof d.estado_especimenes === 'string'
    ? d.estado_especimenes.split(',').map((s: string) => s.trim()).filter(Boolean)
    : Array.isArray(d.estado_especimenes) ? d.estado_especimenes : []

  const estadoStr = estado.length > 0 ? estado.join(', ') : v(d.estado_especimenes)
  const tensionPrevia = estado.includes('Con tensión previa') ? 'Sí' : 'No aplica'
  const rechazados = v(d.especimenes_rechazados)
  const hayRechazados = rechazados && rechazados !== '0' && rechazados !== ''
  const reportaCV = d.reportar !== false && d.reportar !== 'false'

  const TABLE_MAP: Record<string, { label: string; unit: string }> = {
    fm:  { label: 'Fuerza máxima',                  unit: 'N' },
    fr:  { label: 'Fuerza a la rotura',              unit: 'N' },
    afm: { label: 'Alargamiento a la Fuerza máxima', unit: '%' },
    ar:  { label: 'Alargamiento a la rotura',         unit: '%' },
  }

  const resultRows = tablas.map((t: string) => {
    const info = TABLE_MAP[t] || { label: t, unit: '' }
    const redU = v(d[`${t}_red_u`]) ? `${v(d[`${t}_red_u`])} ${info.unit}` : ''
    const redT = v(d[`${t}_red_t`]) ? `${v(d[`${t}_red_t`])} ${info.unit}` : ''
    const cvU = reportaCV ? rCV059_1(d, t, 'u') : ''
    const cvT = reportaCV ? rCV059_1(d, t, 't') : ''
    return `<tr>
      <td class="bold" colspan="2" style="padding:6px">${info.label}</td>
      <td class="center" colspan="2" style="padding:6px">${redU}${reportaCV && cvU ? ` (CV: ${cvU}%)` : ''}</td>
      <td class="center" colspan="2" style="padding:6px">${redT}${reportaCV && cvT ? ` (CV: ${cvT}%)` : ''}</td>
    </tr>`
  }).join('')

  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="5" class="center bold">Determinación de la fuerza máxima de tracción por el método de la tira<br>NMX-A-059/1-INNTEX-2019</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr>
      <td>Fecha de ensayo:</td>
      <td><strong>${v(d.fecha)}</strong></td>
      <td colspan="2">Número de especímenes ensayados:</td>
      <td colspan="2">${v(d.num_especimenes) ? `${v(d.num_especimenes)} en urdimbre y ${v(d.num_especimenes)} en trama` : ''}</td>
    </tr>
    <tr>
      <td>Longitud del ensayo:</td>
      <td><strong>${v(d.longitud_ensayo)} mm</strong></td>
      <td>Especímenes rechazados:</td>
      <td><strong>${hayRechazados ? rechazados : 'Ninguno'}</strong></td>
      <td>Motivo:</td>
      <td><strong>${hayRechazados ? '' : 'No Aplica'}</strong></td>
    </tr>
    <tr>
      <td>Velocidad de alargamiento (mm/min)</td>
      <td><strong>${v(d.velocidad_alargamiento)}</strong></td>
      <td colspan="2">Tensión previa aplicada</td>
      <td colspan="2"><strong>${tensionPrevia}</strong></td>
    </tr>
    <tr>
      <td>Ancho de tira /mm</td>
      <td><strong>${v(d.ancho_tira)}</strong></td>
      <td>No. de hilos</td>
      <td><strong>${v(d.num_hilos_tira)}</strong></td>
      <td>Estado de los especímenes</td>
      <td><strong>${estadoStr}</strong></td>
    </tr>
    <tr>
      <td>Desviación al método:</td>
      <td colspan="5" class="center"><strong>${v(d.desv_metodo) || 'Ninguna'}</strong></td>
    </tr>
    ${d.tiene_metodo_muestreo ? `<tr>
      <td>Método de toma de muestreo:</td>
      <td colspan="5"><strong>${v(d.metodo_muestreo)}</strong></td>
    </tr>` : ''}
    <tr><td colspan="6" class="center bold">RESULTADO</td></tr>
    <tr>
      <td colspan="2"></td>
      <td colspan="4" class="center bold">Promedio</td>
    </tr>
    <tr>
      <td colspan="2"></td>
      <td colspan="2" class="center bold">Urdimbre (Longitudinal)</td>
      <td colspan="2" class="center bold">Trama (Transversal)</td>
    </tr>
    ${resultRows}
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="6" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE AATCC TM127 — Resistencia al agua: presión hidrostática
function seccionAATCC127(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="5" class="center bold">Test Method for Water Resistance: Hydrostatic Pressure TM127-2017 (2018) e</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr>
      <td>Fecha</td>
      <td class="bold">${v(d.fecha)}</td>
      <td>Temperatura de agua</td>
      <td>${v(d.temp_agua)} °C</td>
      <td>Tipo de agua</td>
      <td>${v(d.tipo_agua)}</td>
    </tr>
    <tr>
      <td>Material del especimen</td>
      <td colspan="2" class="center bold">${v(d.tipo_material)}</td>
      <td>Cara Delantera Probada</td>
      <td colspan="2">${v(d.cara_delantera)}</td>
    </tr>
    <tr>
      <td>Gradiente (mbar/min)</td>
      <td class="bold">60</td>
      <td>Tipo de equipo</td>
      <td colspan="3" class="bold">Equipo de cabeza hidrostática (Opción 2)</td>
    </tr>
    <tr>
      <td colspan="2">Desviación al método</td>
      <td colspan="4" class="center">${v(d.desv_metodo) || 'Ninguna'}</td>
    </tr>
    <tr><td colspan="6" class="center bold">RESULTADO</td></tr>
    <tr>
      <td colspan="2" rowspan="2" class="center">Especímen</td>
      <td class="center">1</td>
      <td class="center">2</td>
      <td class="center">3</td>
      <td class="center bold">Promedio</td>
    </tr>
    <tr>
      <td class="center">${v(d.no1)}</td>
      <td class="center">${v(d.no2)}</td>
      <td class="center">${v(d.no3)}</td>
      <td class="center bold">${v(d.promedio) ? `${v(d.promedio)} cm/H₂O` : ''}</td>
    </tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="6" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// CLIENTE NMX-S-051 — Resistencia a la flexión
function seccionS051(d: any): string {
  return `<div class="no-break seccion"><table>
    <tr class="th-row">
      <td colspan="3" class="center bold">Resistencia a la flexión en cuero Numeral 7.1.1<br>NMX-S-051-1989</td>
      <td class="center bold">ACREDITADA</td>
    </tr>
    <tr><td colspan="4">FECHA: ${v(d.fecha)}</td></tr>
    <tr>
      <td colspan="4">Detalles de cualquier variación prescrita en la prueba: <strong>${v(d.desv_metodo) || 'Ninguno'}</strong></td>
    </tr>
    <tr>
      <td colspan="4">Los especímenes fueron evaluados concluyendo los ciclos de flexión tanto en seco (${v(d.flex_prom_seco) || '48 000'}), como en húmedo (${v(d.flex_prom_hum) || '18 000'}).</td>
    </tr>
    <tr><td colspan="4" class="center bold">RESULTADO</td></tr>
    <tr>
      <td colspan="4" style="padding:0;border:none">
        <table style="width:75%;margin:0 auto;border-collapse:collapse">
          <tr>
            <td class="center bold">Especímenes</td>
            <td class="center bold">Flexiones promedio</td>
            <td class="center bold">Resultado</td>
          </tr>
          <tr>
            <td class="center">En seco</td>
            <td class="center">${v(d.flex_prom_seco)}</td>
            <td class="center">${v(d.dano_seco) || 'Tipo de daño'}</td>
          </tr>
          <tr>
            <td class="center">En húmedo</td>
            <td class="center">${v(d.flex_prom_hum)}</td>
            <td class="center">${v(d.dano_hum) || 'Tipo de daño'}</td>
          </tr>
        </table>
      </td>
    </tr>
    ${d._mostrarObs && v(d.observaciones) ? `<tr><td colspan="4" style="text-align:justify">Notas: ${v(d.observaciones)}</td></tr>` : ''}
  </table></div>`
}

// PRUEBA ADICIONAL — solo el nombre de la prueba y el contenido cargado del Word (sin campos de control interno, esos van a la bitácora)
function seccionAdicional(d: any): string {
  const notaDiv = (nota: string | null | undefined) =>
    nota ? `<div style="margin:4px 0 8px 0;padding:4px 6px;font-size:9pt;break-inside:avoid;page-break-inside:avoid">${DOMPurify.sanitize(nota)}</div>` : ''
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="4">${v(d.nombre_prueba) || 'PRUEBA ADICIONAL'}</td></tr>
  </table>
  ${notaDiv(d.nota_doc)}
  </div>`
}

function renderSeccionPrueba(nombre: string, data: any): string {
  if (nombre.includes('NMX-A-3801')) return seccion3801(data)
  if (nombre.includes('NMX-A-109')) return seccion109(data)
  if (nombre.includes('NMX-A-7211')) return seccion7211(data)
  if (nombre.includes('NMX-A-134')) return seccion134(data)
  if (nombre.includes('MET-7.2-11') || nombre.includes('7.2-11')) return seccion711(data)
  if (nombre.includes('MET-7.2-12') || nombre.includes('7.2-12')) return seccion712(data)
  if (nombre.includes('MET-7.2-19') || nombre.includes('7.2-19')) return seccion719(data)
  if (nombre.includes('NMX-A-275')) return seccion275(data)
  if (nombre.includes('NMX-A-172')) return seccion172(data)
  if (nombre.includes('NMX-A-9073')) return seccion9073(data)
  if (nombre.includes('NMX-A-4920')) return seccion4920(data)
  if (nombre.includes('NMX-A-12945')) return seccion12945(data)
  if (nombre.includes('NMX-A-083')) return seccion083(data)
  if (nombre.includes('NMX-A-073')) return seccion073(data)
  if (nombre.includes('NMX-A-105-D01')) return seccion105d01(data)
  if (nombre.includes('NMX-A-105-B02')) return seccion105b02(data)
  if (nombre.includes('NMX-A-105-C06') || (nombre.includes('NMX-A-105') && !nombre.includes('E04') && !nombre.includes('D01') && !nombre.includes('B02'))) return seccion105(data)
  if (nombre.includes('NMX-A-105-E04') || nombre.includes('SUDOR')) return seccion104(data)
  if (nombre.includes('NMX-A-059/1') || nombre.includes('NMX-A-059_1') || nombre.includes('MÉTODO DE LA TIRA')) return seccion059_1(data)
  if (nombre.includes('NMX-A-059/2') || nombre.includes('059/2') || nombre.includes('NMX-A-059_2') || nombre.includes('MÉTODO GRAB') || nombre.includes('Grab')) return seccion059_2(data)
  if (nombre.includes('AATCC') || nombre.includes('TM127') || nombre.includes('HYDROSTATIC')) return seccionAATCC127(data)
  if (nombre.includes('NMX-S-051') || nombre.includes('Resistencia a la flexión')) return seccionS051(data)
  if (nombre.includes('NMX-A-5077')) return seccion5077(data)
  if (nombre.includes('ANÁLISIS QUÍMICO') || nombre.includes('ANALISIS')) return seccionAnalisis(data)
  if (nombre.includes('NMX-A-221')) return seccion221(data)
  if (nombre.includes('NMX-A-225')) return seccion225(data)
  if (nombre.includes('NMX-A-228')) return seccion228(data)
  if (nombre.includes('NMX-A-229')) return seccion229(data)
  if (nombre.includes('NMX-A-230')) return seccion230(data)
  if (nombre.includes('PRUEBA ADICIONAL')) return seccionAdicional(data)
  // Fallback genérico
  return `<div class="no-break seccion"><table>
    <tr class="th-row"><td colspan="2">${nombre}</td>${esAcreditada(nombre) ? '<td class="center bold">ACREDITADA</td>' : '<td></td>'}</tr>
    <tr><td colspan="3">REALIZÓ: ${v(data.realizo)} &nbsp; SUPERVISÓ: ${v(data.superviso)}</td></tr>
  </table></div>`
}

// ─── Función principal ─────────────────────────────────────────────────────

export function generarReporteCliente(datos: DatosReporte): string {
  const { pedido, pruebas } = datos

  const filas = pruebas
    .filter(p => p.incluirReporteCliente)
    .map(p => {
      const es083 = p.nombre.includes('NMX-A-083')
      const rowClass = es083 ? ' class="rc083-row"' : ' style="break-inside:avoid;page-break-inside:avoid"'
      return `<tr${rowClass}><td style="padding:0 30px 14px;border:none">${renderSeccionPrueba(p.nombre, { ...p.data, _mostrarObs: p.mostrarObs })}</td></tr>`
    })
    .join('\n')

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Reporte Cliente - Folio ${v(pedido.folio)}</title>
<style>${CSS}</style>
</head>
<body>
<table style="width:100%;border-collapse:collapse">
  <tbody>
    <tr><td style="padding:0 30px;border:none">${portada(pedido)}</td></tr>
  </tbody>
</table>
${pedido.nota_doc_cliente ? `<div class="pdf-embed" style="padding:0 30px">${DOMPurify.sanitize(pedido.nota_doc_cliente)}</div>` : ''}
<table style="width:100%;border-collapse:collapse">
  <tbody>
    <tr><td style="padding:0 30px;border:none">${leyendaResultados()}</td></tr>
    ${filas}
    <tr><td style="padding:0 30px;border:none">${cierre(pedido)}</td></tr>
  </tbody>
</table>
</body>
</html>`
}
